import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, PlugZap, Save } from 'lucide-react';
import type { ConnectionStatus, McpServerConnection } from '../types';

function statusClass(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'mcp-v2__pill mcp-v2__pill--connected';
    case 'failed':
      return 'mcp-v2__pill mcp-v2__pill--failed';
    case 'testing':
      return 'mcp-v2__pill mcp-v2__pill--testing';
    default:
      return 'mcp-v2__pill mcp-v2__pill--never';
  }
}

function statusLabel(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'failed':
      return 'Failed';
    case 'testing':
      return 'Testing…';
    default:
      return 'Not tested';
  }
}

type Props = {
  connection: McpServerConnection;
  onBack: () => void;
  onTest: () => void;
  onSaveSelections: (
    toolEnabled: Record<string, boolean>,
    resourceEnabled: Record<string, boolean>
  ) => void;
  onEdit: () => void;
};

export default function ConnectionDetail({
  connection,
  onBack,
  onTest,
  onSaveSelections,
  onEdit,
}: Props) {
  const [toolEnabled, setToolEnabled] = useState<Record<string, boolean>>({});
  const [resourceEnabled, setResourceEnabled] = useState<Record<string, boolean>>({});
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const tools: Record<string, boolean> = {};
    for (const t of connection.tools) tools[t.id] = t.enabled;
    const resources: Record<string, boolean> = {};
    for (const r of connection.resources) resources[r.id] = r.enabled;
    setToolEnabled(tools);
    setResourceEnabled(resources);
    setSavedFlash(false);
  }, [connection]);

  const testing = connection.status === 'testing';

  const endpointSummary = useMemo(() => {
    if (connection.transport === 'stdio') {
      const args = (connection.args ?? []).join(' ');
      return `${connection.command ?? ''} ${args}`.trim();
    }
    return connection.url ?? '—';
  }, [connection]);

  const handleSave = () => {
    onSaveSelections(toolEnabled, resourceEnabled);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost mcp-v2__detail-back" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to connections
        </button>
        <div className="mcp-v2__header" style={{ marginTop: '0.5rem' }}>
          <div>
            <p className="mcp-v2__eyebrow">{connection.source} connection</p>
            <h2 className="mcp-v2__title">{connection.name}</h2>
            {connection.description ? (
              <p className="mcp-v2__subtitle">{connection.description}</p>
            ) : null}
            <div className="mcp-v2__meta" style={{ marginTop: 8 }}>
              <span className={statusClass(connection.status)}>{statusLabel(connection.status)}</span>
              <span className="mcp-v2__pill mcp-v2__pill--transport">{connection.transport}</span>
              {connection.tags.map((t) => (
                <span key={t} className="mcp-v2__tag">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="mcp-v2__actions">
            <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={onEdit}>
              Edit config
            </button>
            <button
              type="button"
              className="mcp-v2__btn mcp-v2__btn--primary"
              onClick={onTest}
              disabled={testing}
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : (
                <PlugZap className="w-4 h-4" aria-hidden />
              )}
              {testing ? 'Testing…' : 'Test connection'}
            </button>
          </div>
        </div>
      </div>

      <div className="mcp-v2__panel mcp-v2__detail-section">
        <h3>Configuration</h3>
        <div className="mcp-v2__meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          <span>
            <strong>Endpoint:</strong> {endpointSummary || '—'}
          </span>
          <span>
            <strong>Auth:</strong> {connection.authType}
            {connection.authType !== 'none' && connection.authValue ? ' · configured' : ''}
          </span>
          {connection.lastTestedAt ? (
            <span>
              <strong>Last tested:</strong> {new Date(connection.lastTestedAt).toLocaleString()}
            </span>
          ) : null}
        </div>
        {connection.lastError ? (
          <div className="mcp-v2__alert mcp-v2__alert--error" style={{ marginTop: 12 }}>
            {connection.lastError}
          </div>
        ) : null}
        {connection.status === 'connected' && !connection.lastError ? (
          <div className="mcp-v2__alert mcp-v2__alert--ok" style={{ marginTop: 12 }}>
            Connection succeeded. Review tools and resources below, then save selections to your system.
          </div>
        ) : null}
      </div>

      <div className="mcp-v2__detail-grid">
        <div className="mcp-v2__panel mcp-v2__detail-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 style={{ margin: 0 }}>Tools ({connection.tools.length})</h3>
          </div>
          {connection.tools.length === 0 ? (
            <p style={{ margin: 0, color: '#71717a', fontSize: '0.8125rem' }}>
              Run a successful test to discover tools from this server.
            </p>
          ) : (
            connection.tools.map((t) => (
              <label key={t.id} className="mcp-v2__discover-item mcp-v2__check">
                <input
                  type="checkbox"
                  checked={toolEnabled[t.id] ?? false}
                  onChange={(e) =>
                    setToolEnabled((prev) => ({ ...prev, [t.id]: e.target.checked }))
                  }
                />
                <span>
                  <strong>{t.name}</strong>
                  <span>{t.description}</span>
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{t.inputSchemaSummary}</span>
                </span>
              </label>
            ))
          )}
        </div>

        <div className="mcp-v2__panel mcp-v2__detail-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 style={{ margin: 0 }}>Resources ({connection.resources.length})</h3>
          </div>
          {connection.resources.length === 0 ? (
            <p style={{ margin: 0, color: '#71717a', fontSize: '0.8125rem' }}>
              Run a successful test to discover resources from this server.
            </p>
          ) : (
            connection.resources.map((r) => (
              <label key={r.id} className="mcp-v2__discover-item mcp-v2__check">
                <input
                  type="checkbox"
                  checked={resourceEnabled[r.id] ?? false}
                  onChange={(e) =>
                    setResourceEnabled((prev) => ({ ...prev, [r.id]: e.target.checked }))
                  }
                />
                <span>
                  <strong>{r.name}</strong>
                  <span>{r.uri}</span>
                  <span>
                    {r.mimeType}
                    {r.description ? ` · ${r.description}` : ''}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="mcp-v2__btn mcp-v2__btn--primary"
          onClick={handleSave}
          disabled={connection.tools.length === 0 && connection.resources.length === 0}
        >
          <Save className="w-4 h-4" aria-hidden />
          Save selections
        </button>
        {savedFlash ? (
          <span className="mcp-v2__alert mcp-v2__alert--ok" style={{ padding: '0.4rem 0.75rem' }}>
            Selections saved (synthetic / local)
          </span>
        ) : null}
      </div>
    </div>
  );
}
